import React, { RefObject } from 'react';
import { EventMarkets } from 'src/domains/sportsbook/webview/components/eventMarkets/EventMarkets';
import { YouDecideHref } from 'src/domains/sportsbook/webview/components/matchCard/youDecideHeader/YouDecideHeaderHref';
import { SelectionRab } from 'src/domains/sportsbook/webview/components/selectionRab/SelectionRab';
import { SelectionRabCorrectScore } from 'src/domains/sportsbook/webview/components/selectionRab/selectionRabCorrectScore/SelectionRabCorrectScore';
import { YouDecide } from 'src/domains/sportsbook/webview/components/youDecide/YouDecide';
import { HtmlElementReactive } from 'src_common/common/mobx-utils/HtmlElementReactive';
import { MarketRabView } from 'src/domains/sportsbook/betting/state/rabState/Types';
import { YouDecideHeader } from 'src/domains/sportsbook/webview/components/matchCard/youDecideHeader/YouDecideHeader';
import { SelectionRabCombined } from 'src/domains/sportsbook/webview/components/selectionRab/selectionRabCombined/SelectionRabCombined';
import { EventsTableMarketsLoader } from 'src/domains/sportsbook/webview/components/loaders/EventsTableMarketsLoader';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { MarketFiltersForSportState } from 'src/domains/sportsbook/state/marketFiltersState/MarketFiltersForSportState';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { BetslipRabId } from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { RabState } from 'src/domains/sportsbook/betting/state/rabState/RabState';
import { MultibetWidget } from './multibetWidget/MultibetWidget';
import {
    RabHeader,
    MatchCardContent,
    MatchCardContentWrapper,
    MatchCardContentMarkets,
    MatchCardContentMarketsRAB,
    MatchCardInfoWrapper,
} from './MatchCard.style';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { MatchCardLoader } from './MatchCardLoader';

import { FilterSecondaryBetBuilder } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondaryBetBuilder';

export const contentMarkets = (
    marketFiltersForSport: MarketFiltersForSportState,
    event: EventModel,
    sportsbookVariant: DefaultComponentVariantType | null
): React.ReactNode => {
    const { marketsForView, activeFilter } = marketFiltersForSport;
    const isInPlay = event.timeSettingsStarted === true;
    // In-play: keep EventMarkets mounted when open markets briefly drop to 0 so coalesce
    // sticky slots survive. Unmounting swaps in a loader and resets sticky state.
    // Still allow the initial loader while event markets have never arrived (`null`).
    const keepEventMarketsMounted = marketsForView.length > 0 || (isInPlay && event.eventMarkets !== null);

    return (
        <MatchCardContent key='match-card-content'>
            {keepEventMarketsMounted ? (
                <EventMarkets
                    // Remount on chip change so line-slot coalesce does not keep previous filter's slots.
                    key={activeFilter}
                    eventId={event.id2}
                    markets={marketsForView}
                />
            ) : event.allMarketsLoading ? (
                sportsbookVariant === DefaultComponentVariantType.SECONDARY ? (
                    <MatchCardLoader />
                ) : (
                    <EventsTableMarketsLoader />
                )
            ) : (
                <MatchCardInfoWrapper>Missing markets</MatchCardInfoWrapper>
            )}
        </MatchCardContent>
    );
};

export const renderViewNormal = (
    marketsWrapperRef: RefObject<HTMLDivElement>,
    contentMarkets: React.ReactNode,
    getMarketsWrapperWidth: number | undefined,
    isYouDecideOpen: boolean,
    isFiltersAvailable: boolean,
    rabAvailable: boolean,
    sportsbookVariant: DefaultComponentVariantType | null
): JSX.Element => {
    return (
        <MatchCardContentWrapper
            ref={marketsWrapperRef}
            variant={sportsbookVariant}
            isYouDecideOpen={isYouDecideOpen}
            isFiltersAvailable={isFiltersAvailable}
        >
            <MatchCardContentMarkets
                variant={sportsbookVariant}
                widthContent={getMarketsWrapperWidth}
                isYouDecideOpen={isYouDecideOpen}
                rabAvailable={rabAvailable}
            >
                {contentMarkets}
            </MatchCardContentMarkets>
        </MatchCardContentWrapper>
    );
};

export const renderViewRab = (
    marketsWrapperRef: RefObject<HTMLDivElement>,
    contentMarkets: React.ReactNode,
    getMarketsWrapperWidth: number | undefined,
    isYouDecideOpen: boolean,
    isFiltersAvailable: boolean,
    rabAvailable: boolean,
    renderContentMarketsRAB: Array<JSX.Element>,
    sportsbookVariant: DefaultComponentVariantType | null,
    event?: EventModel,
    isMultibetWidgetEnabled?: boolean
): JSX.Element => {
    if (isMultibetWidgetEnabled === true && event !== undefined && rabAvailable === true && isYouDecideOpen === true) {
        return (
            <MatchCardContentWrapper
                ref={marketsWrapperRef}
                variant={sportsbookVariant}
                isYouDecideOpen={isYouDecideOpen}
                isFiltersAvailable={isFiltersAvailable}
            >
                <MultibetWidget
                    event={event}
                    isOpen={isYouDecideOpen}
                />
            </MatchCardContentWrapper>
        );
    }

    // Original RAB implementation
    return (
        <MatchCardContentWrapper
            ref={marketsWrapperRef}
            variant={sportsbookVariant}
            isYouDecideOpen={isYouDecideOpen}
            isFiltersAvailable={isFiltersAvailable}
        >
            <MatchCardContentMarkets
                variant={sportsbookVariant}
                widthContent={getMarketsWrapperWidth}
                isYouDecideOpen={isYouDecideOpen}
                rabAvailable={rabAvailable}
            >
                {contentMarkets}
            </MatchCardContentMarkets>
            {rabAvailable ? (
                <MatchCardContentMarketsRAB
                    variant={sportsbookVariant}
                    widthContent={getMarketsWrapperWidth}
                    isYouDecideOpen={isYouDecideOpen}
                    rabAvailable={rabAvailable}
                >
                    {renderContentMarketsRAB}
                </MatchCardContentMarketsRAB>
            ) : null}
        </MatchCardContentWrapper>
    );
};

export const mapItem = (item: MarketRabView, index: number, rabId: BetslipRabId): JSX.Element | null => {
    if (item.viewDetails.selectionDisplayType === 'CorrectScore') {
        return (
            <SelectionRabCorrectScore
                rabId={rabId}
                rabMarket={item}
                key={item.name}
                order={index}
            />
        );
    }

    if (item.templateType === 'FirstNextGoalScorer') {
        return (
            <SelectionRabCombined
                templateType={item.templateType}
                rabId={rabId}
                marketName='Player to score'
                order={index}
                firstMarketName='First/next goalscorer'
                secondMarketName='Anytime scorer'
            />
        );
    }

    if (item.templateType === 'FirstNextCardedPlayer') {
        return (
            <SelectionRabCombined
                templateType={item.templateType}
                rabId={rabId}
                marketName='Player to be carded'
                order={index}
                firstMarketName='First/next carded player'
                secondMarketName='Anytime carded player'
            />
        );
    }

    if (item.templateType === 'AnytimeScorer' || item.templateType === 'AnytimeCardedPlayer') {
        return null;
    }

    return (
        <SelectionRab
            rabId={rabId}
            rabMarket={item}
            key={item.name}
            order={index}
        />
    );
};

export const renderContentMarketsRAB = (rabId: BetslipRabId): Array<JSX.Element> => {
    const marketsForView = rabId.getModel().marketsForView;

    const result: Array<JSX.Element> = [];

    for (const [index, item] of marketsForView.entries()) {
        const itemElement = mapItem(item, index, rabId);

        if (itemElement !== null) {
            result.push(itemElement);
        }
    }

    return result;
};

export const renderRABBottomBetslip = (
    reactiveWrapper: HtmlElementReactive<HTMLElement>,
    isFooter: boolean,
    rabId: BetslipRabId
): JSX.Element | null => {
    return (
        <YouDecide
            setRef={reactiveWrapper}
            isFooterVisible={isFooter}
            rabId={rabId}
        />
    );
};

export const renderRABHeader = (
    rabState: RabState,
    eventId: EventId,
    isYouDecideOpen: boolean,
    sportsbookVariant: DefaultComponentVariantType | null
): JSX.Element => {
    const onYouDecideClick = (): void => {
        rabState.toggleOpenForEvent(eventId);
    };
    return (
        <>
            <YouDecideHeader />
            <YouDecideHref
                onClick={onYouDecideClick}
                isYouDecideOpen={isYouDecideOpen}
                variant={sportsbookVariant}
            />
        </>
    );
};

export const renderRAB = (
    rabState: RabState,
    eventId: EventId,
    sportsbookVariant: DefaultComponentVariantType | null
): JSX.Element => {
    const { isBetBuilderOpen: isYouDecideOpen } = rabState;
    return (
        <RabHeader
            isYouDecideOpen={isYouDecideOpen}
            variant={sportsbookVariant}
        >
            {renderRABHeader(rabState, eventId, isYouDecideOpen, sportsbookVariant)}
        </RabHeader>
    );
};

export const renderBetBuilderButton = (rabState: RabState, eventId: EventId): JSX.Element => {
    const { isBetBuilderOpen } = rabState;

    const onBetBuilerClick = (): void => {
        rabState.toggleOpenForEvent(eventId);
    };
    return (
        <FilterSecondaryBetBuilder
            isActive={isBetBuilderOpen}
            onClick={onBetBuilerClick}
        />
    );
};
